import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    if (this.presentation === 'carousel') {
      this.refs.slideshow?.addEventListener('slideshow:select', this.#handleSlideSelect, { signal });
      window.addEventListener('resize', this.#handleResize, { signal });

      // Set initial height after layout is calculated
      requestAnimationFrame(() => {
        const slideshow = this.refs.slideshow;
        const initialSlide = slideshow?.slides?.[slideshow?.current ?? 0];
        this.#updateSliderHeight(initialSlide);
      });
    }
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  #handleSlideSelect = (event) => {
    this.#updateSliderHeight(event.detail.slide);
  };

  #handleResize = () => {
    const slideshow = this.refs.slideshow;
    const currentSlide = slideshow?.slides?.[slideshow?.current ?? 0];
    this.#updateSliderHeight(currentSlide);
  };

  /**
   * Updates the slideshow scroller height to match the active slide's aspect ratio.
   * Reads --media-preview-ratio directly from the inline style (set by Liquid in adapt mode).
   * @param {HTMLElement | undefined} slide
   */
  #updateSliderHeight(slide) {
    const scroller = this.refs.slideshow?.refs?.scroller;
    if (!scroller || !slide) return;

    // Primary: read from inline CSS var (reliable on all browsers including iOS Safari)
    let ratio = parseFloat(slide.style.getPropertyValue('--media-preview-ratio'));

    // Fallback: parse getComputedStyle for non-adapt mode with fixed ratios like "1 / 1"
    if (!ratio || isNaN(ratio)) {
      const computed = getComputedStyle(slide).aspectRatio;
      if (!computed || computed === 'auto') return;

      if (computed.includes('/')) {
        const [w, h] = computed.split('/').map((s) => parseFloat(s.trim()));
        ratio = w / h;
      } else {
        ratio = parseFloat(computed);
      }
    }

    if (!ratio || isNaN(ratio)) return;

    scroller.style.height = `${Math.round(scroller.offsetWidth / ratio)}px`;
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
